import { useState } from 'react';
import { simulate, type SimulateRequest, type SimulateResponse } from '../api/client';

const ROUTE_CLASSES = ['SIGNUP', 'LOGIN', 'FORGOT_PASSWORD', 'OTP_START', 'OTP_VERIFY'];

export default function SimulatePage() {
  const [request, setRequest] = useState<SimulateRequest>({
    route_class: 'OTP_START',
    ip: '203.0.113.1',
    user_agent:
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
    country: 'US',
  });
  const [result, setResult] = useState<SimulateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSimulate() {
    setLoading(true);
    setError(null);
    setResult(null);

    const response = await simulate(request);

    if (response.error) {
      setError(response.error);
    } else if (response.data) {
      setResult(response.data);
    }

    setLoading(false);
  }

  function VerdictDisplay({ verdict, score }: { verdict: string; score: number }) {
    const colors: Record<string, string> = {
      ALLOW: 'bg-green-500',
      CHALLENGE: 'bg-yellow-500',
      BLOCK: 'bg-red-500',
      THROTTLE: 'bg-orange-500',
    };

    return (
      <div className="flex items-center gap-4">
        <div className={`${colors[verdict] || 'bg-gray-500'} px-6 py-4 rounded-lg`}>
          <div className="text-2xl font-bold text-white">{verdict}</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold">{score}</div>
          <div className="text-gray-400 text-sm">Risk Score</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Simulate Decision</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input form */}
        <div className="card">
          <h2 className="card-header">Request Parameters</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Route Class *</label>
              <select
                className="select"
                value={request.route_class}
                onChange={(e) => setRequest({ ...request, route_class: e.target.value })}
              >
                {ROUTE_CLASSES.map((rc) => (
                  <option key={rc} value={rc}>
                    {rc}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">IP Address *</label>
              <input
                type="text"
                className="input"
                placeholder="203.0.113.1"
                value={request.ip}
                onChange={(e) => setRequest({ ...request, ip: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">User Agent *</label>
              <textarea
                className="input"
                rows={2}
                value={request.user_agent}
                onChange={(e) => setRequest({ ...request, user_agent: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Country</label>
                <input
                  type="text"
                  className="input"
                  placeholder="US"
                  value={request.country || ''}
                  onChange={(e) => setRequest({ ...request, country: e.target.value || undefined })}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">ASN</label>
                <input
                  type="text"
                  className="input"
                  placeholder="AS12345"
                  value={request.asn || ''}
                  onChange={(e) => setRequest({ ...request, asn: e.target.value || undefined })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Device ID Hash</label>
              <input
                type="text"
                className="input font-mono"
                placeholder="abc123def456..."
                value={request.device_id_hash || ''}
                onChange={(e) =>
                  setRequest({ ...request, device_id_hash: e.target.value || undefined })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email Hash</label>
                <input
                  type="text"
                  className="input font-mono"
                  placeholder="hash..."
                  value={request.email_hash || ''}
                  onChange={(e) =>
                    setRequest({ ...request, email_hash: e.target.value || undefined })
                  }
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone Hash</label>
                <input
                  type="text"
                  className="input font-mono"
                  placeholder="hash..."
                  value={request.phone_hash || ''}
                  onChange={(e) =>
                    setRequest({ ...request, phone_hash: e.target.value || undefined })
                  }
                />
              </div>
            </div>

            <button
              className="btn btn-primary w-full"
              onClick={handleSimulate}
              disabled={loading}
            >
              {loading ? 'Simulating...' : 'Run Simulation'}
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {error && (
            <div className="bg-red-500/20 text-red-400 p-4 rounded-lg">
              Error: {error}
            </div>
          )}

          {result && (
            <>
              {/* Verdict */}
              <div className="card">
                <h2 className="card-header">Decision</h2>
                <VerdictDisplay
                  verdict={result.decision.verdict}
                  score={result.decision.risk_score}
                />
              </div>

              {/* Reasons */}
              <div className="card">
                <h2 className="card-header">Reasons</h2>
                {result.decision.reasons.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {result.decision.reasons.map((reason) => (
                      <span key={reason} className="bg-gray-700 px-3 py-1 rounded text-sm">
                        {reason}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-400">No risk reasons detected</div>
                )}
              </div>

              {/* Score breakdown */}
              <div className="card">
                <h2 className="card-header">Score Breakdown</h2>
                {result.score_breakdown.length > 0 ? (
                  <div className="space-y-2">
                    {result.score_breakdown.map((item, i) => (
                      <div key={i} className="flex justify-between items-center">
                        <span className="text-gray-300">{item.reason}</span>
                        <span
                          className={`font-mono ${
                            item.score_contribution > 0
                              ? 'text-red-400'
                              : item.score_contribution < 0
                              ? 'text-green-400'
                              : 'text-gray-400'
                          }`}
                        >
                          {item.score_contribution > 0 ? '+' : ''}
                          {item.score_contribution}
                        </span>
                      </div>
                    ))}
                    <div className="border-t border-gray-700 pt-2 flex justify-between items-center font-bold">
                      <span>Total</span>
                      <span>{result.decision.risk_score}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400">No score contributions</div>
                )}
              </div>

              {/* Signals (collapsible) */}
              <details className="card">
                <summary className="cursor-pointer font-semibold">
                  Raw Signals (JSON)
                </summary>
                <pre className="mt-4 bg-gray-900 p-4 rounded text-xs overflow-auto max-h-64">
                  {JSON.stringify(result.signals, null, 2)}
                </pre>
              </details>
            </>
          )}

          {!result && !error && (
            <div className="card text-center text-gray-400 py-12">
              Run a simulation to see results
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
